/**
 * Export Audit Logs Modal
 *
 * Modal component for exporting audit logs to CSV.
 * Allows selecting a date range and optional object type filter.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  DatePicker,
  Text,
  Stack,
  Banner,
  Select,
  useModal,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import type { CalendarDate } from '@internationalized/date';
import { today, getLocalTimeZone } from '@internationalized/date';
import { useServicesMutation } from '@/services/api';
import { useAnalytics } from '@/services/analytics';
import { generatePath } from '@/services/router/generate-path';
// Max date range is 1 year (365 days) as enforced by the backend
const MAX_DATE_RANGE_DAYS = 365;

// Default to last 30 days
const DEFAULT_DATE_RANGE_DAYS = 30;

// Sentinel value for "no filter" in the object type dropdown
const ALL_OBJECT_TYPES_FILTER = 'all';

/**
 * Build the exports page URL.
 * All exports are accessed through the workspace-scoped Journeys exports page.
 */
function getExportsPageUrl(workspaceId: number): string {
  return generatePath('/workspaces/:workspaceId/journeys/exports', { workspaceId });
}

/**
 * Calculate the number of days between two CalendarDate objects.
 * Uses UTC to avoid timezone-related calculation issues.
 * Returns a positive number when end >= start, negative when end < start.
 */
function getDaysBetween(start: CalendarDate, end: CalendarDate): number {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  // Convert to UTC dates at midnight to ensure consistent day calculation
  const startUtc = Date.UTC(start.year, start.month - 1, start.day);
  const endUtc = Date.UTC(end.year, end.month - 1, end.day);
  return Math.round((endUtc - startUtc) / MS_PER_DAY);
}

interface Workspace {
  id: number;
  name: string;
}

interface ExportAuditLogsModalData {
  context: 'account' | 'workspace';
  contextId: number | null;
  /** Workspace ID for linking to the exports page (from current workspace context) */
  workspaceId: number | null;
  /** List of available workspaces (for account-level exports when no current workspace) */
  workspaces: Workspace[];
  /** Optional object type filter from current table filters */
  currentObjectType?: string;
}

export function ExportAuditLogsModal({ modalState, data, onClose }: PlumaManagedModalProps<ExportAuditLogsModalData>) {
  const { context, contextId, workspaceId, workspaces, currentObjectType } = data;
  const { track } = useAnalytics();

  // Helper to get fresh default date range (last 30 days from current date)
  const getDefaultDateRange = useCallback(() => {
    const endDate = today(getLocalTimeZone());
    const startDate = endDate.subtract({ days: DEFAULT_DATE_RANGE_DAYS });
    return { start: startDate, end: endDate };
  }, []);

  const [dateRange, setDateRange] = useState<{ start: CalendarDate; end: CalendarDate } | null>(() =>
    getDefaultDateRange(),
  );
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(null);

  // Export mutations
  const accountExportMutation = useServicesMutation('exportAccountAuditLogs');
  const workspaceExportMutation = useServicesMutation('exportWorkspaceAuditLogs');
  const activeMutation = context === 'account' ? accountExportMutation : workspaceExportMutation;

  // Show workspace picker for account-level exports when multiple workspaces exist
  // For workspace-level exports, always use the current workspace (no picker needed)
  const showWorkspacePicker = context === 'account' && workspaces.length > 1;

  // Determine the effective workspace ID for the exports link
  // For account context with picker: use selected or default to provided workspaceId
  // For workspace context: always use the provided workspaceId
  // For single workspace: auto-select it
  const effectiveWorkspaceId = useMemo(() => {
    if (showWorkspacePicker) {
      // User must select (or we default to current workspace context if available)
      return selectedWorkspaceId ?? workspaceId;
    }
    if (workspaceId != null) {
      return workspaceId;
    }
    if (workspaces.length === 1) {
      return workspaces[0].id;
    }
    return null;
  }, [showWorkspacePicker, selectedWorkspaceId, workspaceId, workspaces]);

  // Workspace options for the select dropdown
  const workspaceOptions = useMemo(() => {
    return workspaces.map((ws) => ({
      value: ws.id.toString(),
      label: ws.name,
    }));
  }, [workspaces]);

  // Reset state when modal opens - compute fresh date range each time
  React.useEffect(() => {
    if (modalState.isOpen === true) {
      setDateRange(getDefaultDateRange());
      setSelectedWorkspaceId(null);
      // Reset mutations only when the modal is (re)opened to avoid clearing success state mid-session
      accountExportMutation.reset();
      workspaceExportMutation.reset();
    }
    // Intentionally omit mutation objects from deps to avoid resetting on mutation state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.isOpen, getDefaultDateRange]);

  // Validate date range
  const dateRangeError = useMemo(() => {
    if (dateRange == null || dateRange.start == null || dateRange.end == null) {
      return 'Please select a date range';
    }

    // Validate that start is not after end
    if (dateRange.start.compare(dateRange.end) > 0) {
      return 'Start date must be before or equal to end date';
    }

    // After validation, we know start <= end, so diffDays will be >= 0
    const diffDays = getDaysBetween(dateRange.start, dateRange.end);
    if (diffDays > MAX_DATE_RANGE_DAYS) {
      return `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days (1 year)`;
    }

    return null;
  }, [dateRange]);

  const isValidDateRange =
    dateRange != null && dateRange.start != null && dateRange.end != null && dateRangeError == null;

  const handleExport = useCallback(async () => {
    // Prevent duplicate exports if already in progress
    if (activeMutation.isPending === true) return;
    // Require a valid context id before sending the request
    if (contextId == null || Number.isNaN(contextId) || contextId <= 0) return;
    if (!isValidDateRange || dateRange == null) return;
    // For account exports, require a workspace to create the export in
    if (context === 'account' && effectiveWorkspaceId == null) return;

    try {
      // Build export request body
      // object_type is only included if a specific type is selected (not the "all" sentinel value)
      const hasObjectTypeFilter = currentObjectType != null && currentObjectType !== ALL_OBJECT_TYPES_FILTER;

      const body = {
        start_time: `${dateRange.start.toString()}T00:00:00Z`,
        end_time: `${dateRange.end.toString()}T23:59:59Z`,
        object_type: hasObjectTypeFilter ? currentObjectType : undefined,
        // For account exports, include the workspace ID where the export will be created
        environment_id: context === 'account' ? effectiveWorkspaceId ?? undefined : undefined,
      };

      await (context === 'account'
        ? accountExportMutation.mutateAsync({
            params: { account_id: contextId },
            body,
          })
        : workspaceExportMutation.mutateAsync({
            params: { environment_id: contextId },
            body,
          }));

      track('auditLogs', 'exportCreated', {
        context,
        contextId,
        startTime: body.start_time,
        endTime: body.end_time,
        objectType: body.object_type ?? 'all',
      });
    } catch (error) {
      track('auditLogs', 'exportFailed', {
        context,
        contextId,
        error: error instanceof Error ? error.message : 'unknown',
      });
    }
  }, [
    isValidDateRange,
    dateRange,
    context,
    contextId,
    effectiveWorkspaceId,
    currentObjectType,
    accountExportMutation,
    workspaceExportMutation,
    activeMutation,
    track,
  ]);

  const handleClose = useCallback(() => {
    accountExportMutation.reset();
    workspaceExportMutation.reset();
    onClose();
  }, [accountExportMutation, workspaceExportMutation, onClose]);

  const isExporting = activeMutation.isPending === true;
  const isSuccess = activeMutation.isSuccess === true;
  const isError = activeMutation.isError === true;
  const exportErrorMessage =
    activeMutation.error instanceof Error
      ? activeMutation.error.message
      : activeMutation.error != null && typeof (activeMutation.error as { message?: unknown }).message === 'string'
      ? (activeMutation.error as { message?: string }).message
      : 'Failed to create export. Please try again.';

  return (
    <Modal state={modalState} onClose={handleClose} title="Export Audit Logs to CSV" size="sm">
      <ModalBody>
        <Stack gap="300">
          {isSuccess ? (
            <Banner variant="success" title="Export started!">
              <Text>
                Your export has been queued.{' '}
                {effectiveWorkspaceId != null ? (
                  <>
                    You can download it from the{' '}
                    {/* Using plain anchor for Ember route navigation (Link component uses React Router) */}
                    <a href={getExportsPageUrl(effectiveWorkspaceId)} style={{ textDecoration: 'underline' }}>
                      Exports page
                    </a>{' '}
                    once it's ready.
                  </>
                ) : (
                  "You can download it from the Exports page in any workspace once it's ready."
                )}
              </Text>
            </Banner>
          ) : isError ? (
            <Banner variant="error" title="Export failed">
              <Text>{exportErrorMessage}</Text>
            </Banner>
          ) : (
            <>
              <Text>
                Select a date range for the audit logs you want to export. The export will be processed in the
                background and available for download on the Exports page.
              </Text>

              <DatePicker
                label="Date Range"
                isRange={true}
                withPresets={true}
                value={dateRange}
                onChange={(value) => {
                  setDateRange(value as { start: CalendarDate; end: CalendarDate } | null);
                }}
              />

              {dateRangeError != null ? (
                <Text color="critical" size="s">
                  {dateRangeError}
                </Text>
              ) : null}

              {showWorkspacePicker ? (
                <Select
                  label="Download location"
                  description="Choose which workspace's Exports page to download from"
                  options={workspaceOptions}
                  value={effectiveWorkspaceId?.toString() ?? null}
                  onChange={(value) => {
                    if (typeof value !== 'string' || value === '') {
                      setSelectedWorkspaceId(null);
                      return;
                    }
                    const parsed = Number.parseInt(value, 10);
                    setSelectedWorkspaceId(Number.isNaN(parsed) ? null : parsed);
                  }}
                  placeholder="Select a workspace"
                  isSearchable={true}
                />
              ) : null}

              {currentObjectType != null && currentObjectType !== ALL_OBJECT_TYPES_FILTER ? (
                <Text color="subtle" size="s">
                  This export will include only <strong>{currentObjectType}</strong> events based on your current
                  filter.
                </Text>
              ) : (
                <Text color="subtle" size="s">
                  This export will include all event types.
                </Text>
              )}
            </>
          )}
        </Stack>
      </ModalBody>
      <ModalFooter>
        {isSuccess ? (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            {effectiveWorkspaceId != null ? (
              <Button variant="primary" onClick={() => window.open(getExportsPageUrl(effectiveWorkspaceId), '_blank')}>
                Go to Exports
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} isDisabled={isExporting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              isDisabled={
                !isValidDateRange ||
                isExporting ||
                contextId == null ||
                Number.isNaN(contextId) ||
                contextId <= 0 ||
                (context === 'account' && effectiveWorkspaceId == null)
              }
              isLoading={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export to CSV'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

export function useExportAuditLogsModal() {
  return useModal(ExportAuditLogsModal);
}
